import { ServiceMapStatusCode } from "../services/service-map-service";
import fallbackImage from "../../../assets/images/notify-img-default.png";
import EmptyState from "../../../components/custom/empty-state";
import { useViewportMaxHeight } from "../../../hooks/useViewportMaxHeight";
import { formatCurrency } from "../../../utils/helpers";

export interface ServiceMapRow {
  id: number;
  serviceId: number;
  branchId: number;
  /** Nhóm dịch vụ (services). */
  serviceName: string;
  serviceCode?: string;
  serviceTypeId: number;
  /** Dịch vụ (service_types). */
  serviceTypeName: string;
  serviceTypeCode?: string;
  storeName?: string;
  price: number;
  statusCode: number;
  imageUrl?: string;

  // --- Tính tiền theo dòng (product/docs/vat-tu-va-tinh-tien-ky-thuat.md) ---
  unitId?: number;
  unitName?: string;
  weightPerUnit?: number;
  kind?: number;
  /** CSV id loại đồ liên kết. */
  linkedCategoryIds?: string | null;
}

const PAGE_SIZE_OPTIONS = [20, 50, 100];

interface Props {
  rows: ServiceMapRow[];
  isLoading?: boolean;
  /** Số thứ tự của dòng đầu trang (đã tính theo trang). */
  startIndex?: number;
  onEdit?: (row: ServiceMapRow) => void;
  onActivate?: (row: ServiceMapRow) => void;
  onDeactivate?: (row: ServiceMapRow) => void;
  page: number;
  pageSize: number;
  search: string;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setSearch: (search: string) => void;
  totalRecords: number;
}

/** Mỗi dịch vụ (1 dòng bảng giá) là 1 hàng độc lập; nhóm dịch vụ chỉ là 1 cột hiển thị. */
export default function ServiceMapTable({
  rows,
  isLoading = false,
  startIndex = 1,
  onEdit,
  onActivate,
  onDeactivate,
  page,
  pageSize,
  search,
  setPage,
  setPageSize,
  setSearch,
  totalRecords,
}: Props) {
  const totalPages = Math.ceil(totalRecords / pageSize);
  // Khung bảng cao vừa tới đáy viewport, cuộn bên trong, tiêu đề cột đứng yên. `containerRef` bao cả thanh
  // phân trang để hook chừa chỗ cho nó; 40 = padding dưới của thẻ trắng (24) + padding của layout (16).
  const { ref: frameRef, containerRef, maxHeight } = useViewportMaxHeight<HTMLDivElement>(40);
  const th = "border-b border-r border-gray-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide";

  return (
    <div ref={containerRef}>
      {/* Controls */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex w-full max-w-md items-center gap-2">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Tìm theo tên / mã dịch vụ hoặc tên nhóm..."
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            type="search"
          />
        </div>
      </div>

      {/* Table */}
      <div ref={frameRef} style={{ maxHeight }} className="mt-6 overflow-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full border-separate border-spacing-0 text-left text-sm [&_tr>*:last-child]:border-r-0 [&_tbody>tr:last-child>td]:border-b-0">
          <thead className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-gray-100">
            <tr className="text-slate-600">
              <th className={`w-16 text-center ${th}`}>STT</th>
              <th className={th}>Nhóm dịch vụ</th>
              <th className={th}>Dịch vụ</th>
              <th className={`w-28 ${th}`}>Đơn vị</th>
              <th className={`w-36 text-right ${th}`}>Giá</th>
              <th className={`w-32 text-center ${th}`}>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="border-b border-r border-gray-200 px-4 py-16 text-center text-sm text-slate-400">
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="border-b border-r border-gray-200">
                  <EmptyState message="Chưa có dịch vụ nào" description="Không tìm thấy dịch vụ khớp bộ lọc." />
                </td>
              </tr>
            ) : (
              rows.map((r, i) => {
                const isActive = r.statusCode === ServiceMapStatusCode.ACTIVE;
                const linkedCount = (r.linkedCategoryIds || "").split(",").filter(Boolean).length;
                return (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="border-b border-r border-gray-200 px-4 py-3 text-center font-semibold text-slate-600">{startIndex + i}</td>

                    {/* Nhóm dịch vụ */}
                    <td className="border-b border-r border-gray-200 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={r.imageUrl || fallbackImage}
                          alt={r.serviceName}
                          className="h-9 w-9 rounded-lg object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.src = fallbackImage;
                          }}
                        />
                        <span className="text-slate-700">{r.serviceName}</span>
                      </div>
                    </td>

                    {/* Dịch vụ */}
                    <td className="border-b border-r border-gray-200 px-4 py-3">
                      <div className="font-medium text-slate-800">{r.serviceTypeName}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                        {r.serviceTypeCode}
                        {linkedCount > 0 && (
                          <span
                            className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-600"
                            title="Số loại đồ đã liên kết — dùng mồi kiểm đếm và gợi ý số lượng"
                          >
                            {linkedCount} loại đồ
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Đơn vị — thiếu thì dòng đó không tính tiền được, nên phải thấy ngay ở danh sách */}
                    <td className="border-b border-r border-gray-200 px-4 py-3">
                      {r.unitName ? (
                        <span className="text-slate-700">{r.unitName}</span>
                      ) : (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-600">chưa có đơn vị</span>
                      )}
                    </td>

                    <td className="border-b border-r border-gray-200 px-4 py-3 text-right font-semibold text-slate-800">{formatCurrency(r.price)}đ</td>

                    {/* Actions */}
                    <td className="border-b border-r border-gray-200 px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        {onEdit && (
                          <button
                            type="button"
                            onClick={() => onEdit(r)}
                            className="grid h-9 w-9 place-items-center rounded-full bg-blue-500 text-white shadow-sm transition hover:bg-blue-600"
                            aria-label="Sửa"
                            title="Sửa"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                              <path d="M21.731 2.269a2.625 2.625 0 0 0-3.714 0l-1.157 1.157 3.714 3.714 1.157-1.157a2.625 2.625 0 0 0 0-3.714Z" />
                              <path d="M19.513 8.199l-3.714-3.714-9.193 9.193a5.25 5.25 0 0 0-1.32 2.214l-.8 2.684a.75.75 0 0 0 .933.933l2.684-.8a5.25 5.25 0 0 0 2.214-1.32l9.193-9.193Z" />
                              <path d="M5.25 5.25h3a.75.75 0 0 1 0 1.5h-3A1.75 1.75 0 0 0 3.5 8.5v10A1.75 1.75 0 0 0 5.25 20.25h10a1.75 1.75 0 0 0 1.75-1.75v-3a.75.75 0 0 1 1.5 0v3A3.25 3.25 0 0 1 15.25 21.75h-10A3.25 3.25 0 0 1 2 18.5v-10A3.25 3.25 0 0 1 5.25 5.25Z" />
                            </svg>
                          </button>
                        )}

                        {isActive && onDeactivate ? (
                          <button
                            type="button"
                            onClick={() => onDeactivate(r)}
                            className="grid h-9 w-9 place-items-center rounded-full bg-amber-500 text-white transition hover:bg-amber-600"
                            aria-label="Tạm ngưng"
                            title="Tạm ngưng"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" className="h-4 w-4">
                              <rect x="8" y="7" width="2.5" height="10" rx="0.75" />
                              <rect x="13.5" y="7" width="2.5" height="10" rx="0.75" />
                            </svg>
                          </button>
                        ) : (
                          !isActive &&
                          onActivate && (
                            <button
                              type="button"
                              onClick={() => onActivate(r)}
                              className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-white transition hover:bg-emerald-600"
                              aria-label="Kích hoạt"
                              title="Kích hoạt"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                                <path d="M6 5.75v12.5a.75.75 0 0 0 1.125.65l10-6.25a.75.75 0 0 0 0-1.3l-10-6.25A.75.75 0 0 0 6 5.75Z" />
                              </svg>
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <label className="flex items-center gap-3 text-sm text-slate-500">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            {PAGE_SIZE_OPTIONS.map((op) => (
              <option key={op} value={op}>
                {op}
              </option>
            ))}
          </select>
          <span>entries</span>
        </label>
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{totalRecords === 0 ? 0 : (page - 1) * pageSize + 1}</span> to{" "}
            <span className="font-medium">{Math.min(page * pageSize, totalRecords)}</span> of{" "}
            <span className="font-medium">{totalRecords}</span> results
          </p>
        </div>
        <div>
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
            >
              <span className="sr-only">Previous</span>
              &lt;
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                aria-current={p === page ? "page" : undefined}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                  p === page
                    ? "z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
            >
              <span className="sr-only">Next</span>
              &gt;
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}
